// shareUtils.js
// Builds and shares offer links via the Web Share API, falling back to the clipboard.

/**
 * Builds the link to share from a /go link. The cid is removed so the sharer's client id is not exposed.
 * @param {string} link - The /go link (already carrying lang, cur, res and utm_campaign)
 * @param {string} [attributor] - Campaign attributor; overrides utm_campaign when set
 * @returns {string|null} - The share URL, or null when there is no absolute link
 */
export function buildShareLink(link, attributor) {
  if (!link) return null;
  try {
    const url = new URL(link);
    url.searchParams.delete('cid');
    if (attributor) {
      url.searchParams.set('utm_campaign', attributor);
    }
    return url.toString();
  } catch (e) {
    return null;
  }
}

/**
 * Attempts to share a URL using the Web Share API,
 * falling back to clipboard if unavailable or cancelled.
 * @param {Object} params
 * @param {string} params.url - The URL to share
 * @param {string} params.title - The title for the share dialog
 * @param {function} params.setToastMsg - Function to set the toast message
 * @param {function} params.setShowToast - Function to show the toast
 * @param {string} params.shareLinkCopiedMsg - Message to show when link is copied
 * @param {string} params.shareLinkCopyFailedMsg - Message to show when copy fails
 */
export async function shareOffer({
  url,
  title,
  setToastMsg,
  setShowToast,
  shareLinkCopiedMsg,
  shareLinkCopyFailedMsg
}) {
  if (navigator.share) {
    try {
      await navigator.share({ title, url });
    } catch (err) {
      // User cancelled or error - fallback to clipboard
      try {
        await navigator.clipboard.writeText(url);
        setToastMsg(shareLinkCopiedMsg);
        setShowToast(true);
      } catch {
        setToastMsg(shareLinkCopyFailedMsg);
        setShowToast(true);
      }
    }
  } else {
    // Web Share API not available - use clipboard
    try {
      await navigator.clipboard.writeText(url);
      setToastMsg(shareLinkCopiedMsg);
      setShowToast(true);
    } catch {
      setToastMsg(shareLinkCopyFailedMsg);
      setShowToast(true);
    }
  }
}
